import type { Table } from '@tanstack/react-table'

import { X } from 'lucide-react'
import { Button } from '~/shadcn/ui/button/button'
import { useIsDesktop } from '~/@mid_ui/layout/useIsDesktop'
import {
  getProjectLoanPurposeOptions,
  interestRateOptions,
  type FilterOptionValues,
} from '~/@mid_ui/table/tableFilterOptions'
import { MultiSelect } from '~/@pages_investing/projects/_projects_filtering/MultiSelect'
import { CollateralSubcategorySelect } from '~/@pages_investing/projects/_projects_filtering/CollateralSubcategorySelect'
import type { ProjectCollateralSubcategory } from '~/@pages_investing/projects/_project_types/projectCollateralSubCategory'
import type { ProjectsItem } from '~/@pages_investing/projects/_project_types/project.types'
import {
  amountTargetOptions,
  getPaymentTypeOptions,
  ltlOptions,
  riskOptions,
} from '../_projects_filtering/projectTableFilterOptions'

type RunningProjectsFiltersProps = {
  table: Table<ProjectsItem>
  colorScheme?: 'dark' | 'light'
}

function ArchivedProjectsFilters({
  table,
  colorScheme,
}: RunningProjectsFiltersProps) {
  const isDesktop = useIsDesktop()

  const closeButton = (
    <Button
      variant="ghost"
      className="self-baseline lg:self-center p-2"
      onClick={() => table.setColumnFilters([])}>
      <X className="w-5 h-5" />
      <span className="ml-1 text-black-900">Resetovať filtre</span>
    </Button>
  )

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row flex-wrap items-center gap-3">
        <MultiSelect
          label="Účel úveru"
          colorScheme={colorScheme}
          options={getProjectLoanPurposeOptions()}
          values={
            table
              .getColumn('loanPurpose')!
              .getFilterValue() as FilterOptionValues<
              ReturnType<typeof getProjectLoanPurposeOptions>
            >
          }
          onChange={table.getColumn('loanPurpose')!.setFilterValue}
        />

        <CollateralSubcategorySelect
          values={
            table
              .getColumn('collateralSubcategory')!
              .getFilterValue() as Array<ProjectCollateralSubcategory>
          }
          colorScheme={colorScheme}
          onChange={table.getColumn('collateralSubcategory')!.setFilterValue}
        />

        <MultiSelect
          label="Úroková sadzba"
          colorScheme={colorScheme}
          options={interestRateOptions}
          values={
            table
              .getColumn('interestRate')!
              .getFilterValue() as FilterOptionValues<
              typeof interestRateOptions
            >
          }
          onChange={table.getColumn('interestRate')!.setFilterValue}
        />

        <MultiSelect
          label="LTL"
          colorScheme={colorScheme}
          options={ltlOptions}
          values={
            table
              .getColumn('ltlPercentage')!
              .getFilterValue() as FilterOptionValues<typeof ltlOptions>
          }
          onChange={table.getColumn('ltlPercentage')!.setFilterValue}
        />

        <MultiSelect
          label="Riziko"
          colorScheme={colorScheme}
          options={riskOptions}
          values={
            table
              .getColumn('riskScore')!
              .getFilterValue() as FilterOptionValues<typeof riskOptions>
          }
          onChange={table.getColumn('riskScore')!.setFilterValue}
        />

        <MultiSelect
          label="Výplata úroku"
          options={getPaymentTypeOptions()}
          colorScheme={colorScheme}
          values={
            table
              .getColumn('paymentTypeId')!
              .getFilterValue() as FilterOptionValues<
              ReturnType<typeof getPaymentTypeOptions>
            >
          }
          onChange={table.getColumn('paymentTypeId')!.setFilterValue}
        />
        <MultiSelect
          label="Úverový rámec"
          options={amountTargetOptions}
          colorScheme={colorScheme}
          values={
            table
              .getColumn('amountTarget')!
              .getFilterValue() as FilterOptionValues<
              typeof amountTargetOptions
            >
          }
          onChange={table.getColumn('amountTarget')!.setFilterValue}
        />

        {isDesktop && closeButton}
      </div>
      {!isDesktop && closeButton}
    </div>
  )
}

export { ArchivedProjectsFilters }
